import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, IconButton, Typography } from '@mui/material';

import AutomationCard from './AutomationCard';
import { useThemeContext } from '../../../global/ThemeContext/ThemeContext';
import { AutomationRule } from '../types/rule';

interface AutomationListProps {
  automations: AutomationRule[];
  onDelete?: (uuid: string) => void;
  onEdit?: (uuid: string) => void;
  loading: boolean;
  onUpdate: () => void;
}

const AutomationList: React.FC<AutomationListProps> = ({
  automations,
  loading,
  onUpdate,
  onDelete,
  onEdit,
}) => {
  const theme = useThemeContext();
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'created_at'>('name');
  const [isAscending, setIsAscending] = useState(true);

  const handleSortToggle = (column: 'name' | 'status' | 'created_at') => {
    if (sortBy === column) {
      setIsAscending(!isAscending);
    } else {
      setSortBy(column);
      setIsAscending(true);
    }
  };

  const sortedAutomations = [...automations].sort((a, b) => {
    let valueA: string | number = '';
    let valueB: string | number = '';

    if (sortBy === 'name') {
      valueA = a.name || '';
      valueB = b.name || '';
      return isAscending
        ? String(valueA).toString().localeCompare(String(valueB))
        : String(valueB).toString().localeCompare(String(valueA));
    }
    if (sortBy === 'status') {
      valueA = a.is_active ? 'Ativo' : 'Inativo';
      valueB = b.is_active ? 'Ativo' : 'Inativo';
      return isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    }
    if (sortBy === 'created_at') {
      const tsA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tsB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return isAscending ? tsA - tsB : tsB - tsA;
    }

    return 0;
  });

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '0.2rem 0.5rem',
          backgroundColor: theme.background.secondary,
          color: theme.text.primary,
          borderRadius: '0.5rem',
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '0.5',
            cursor: 'pointer',
            gap: '0.5rem',
          }}
          onClick={() => handleSortToggle('name')}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Nome da Automação
          </Typography>
          <IconButton size="small" sx={{ padding: 0 }}>
            {sortBy === 'name' &&
              (isAscending ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              ))}
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '0.2',
            justifyContent: 'center',
            cursor: 'pointer',
            gap: '0.5rem',
          }}
          onClick={() => handleSortToggle('status')}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Status
          </Typography>
          <IconButton size="small" sx={{ padding: 0 }}>
            {sortBy === 'status' &&
              (isAscending ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              ))}
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '0.2',
            justifyContent: 'center',
            cursor: 'pointer',
            gap: '0.5rem',
          }}
          onClick={() => handleSortToggle('created_at')}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Data de Criação
          </Typography>
          <IconButton size="small" sx={{ padding: 0 }}>
            {sortBy === 'created_at' &&
              (isAscending ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              ))}
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ flex: '0.1', textAlign: 'center', fontWeight: 500 }}>
          Opções
        </Typography>
      </Box>
      <Box sx={{ width: '100%' }}>
        {loading ? (
          <Typography variant="body2">Carregando automações...</Typography>
        ) : sortedAutomations.length > 0 ? (
          sortedAutomations.map(automation => (
            <AutomationCard
              key={automation.uuid}
              automation={automation}
              onDelete={onDelete}
              onEdit={onEdit}
            />
          ))
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma automação encontrada.
          </Typography>
        )}
      </Box>
    </>
  );
};

export default AutomationList;
